/**
 * Airdrop address list parsing and Merkle tree generation utilities.
 *
 * Uses @repo/clanker-sdk's createMerkleTree for Merkle tree construction.
 * Addresses are normalized via viem's getAddress (EIP-55 checksum).
 */

import type { AirdropRecipient } from "@repo/shared-types";
import { getAddress } from "viem";

/** Total token supply: 1 billion. */
const TOTAL_SUPPLY = 1_000_000_000;

/** Minimum airdrop allocation: 0.25% of supply. */
export const MIN_AIRDROP_AMOUNT = TOTAL_SUPPLY * 0.0025; // 2,500,000

/** Maximum airdrop allocation: 90% of supply. */
export const MAX_AIRDROP_AMOUNT = TOTAL_SUPPLY * 0.9; // 900,000,000

/** Result of parsing an address list from CSV or pasted text. */
export type ParseAddressListResult = {
	valid: AirdropRecipient[];
	errors: string[];
	duplicatesMerged: number;
};

/**
 * Parse and validate a raw address list string.
 *
 * Supports formats:
 * - `address,amount` (CSV)
 * - `address amount` or `address\tamount` (space/tab separated)
 *
 * Addresses are normalized to EIP-55 checksummed format.
 * Duplicate addresses are merged by summing amounts.
 */
export function parseAddressList(input: string): ParseAddressListResult {
	const lines = input
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);

	const errors: string[] = [];
	const addressMap = new Map<string, number>();
	let duplicatesMerged = 0;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		if (!line) continue;

		// Skip header row
		if (i === 0 && /^address[,\s]/i.test(line)) {
			continue;
		}

		// Split by comma, space, or tab
		const parts = line.split(/[,\s\t]+/).filter((p) => p.length > 0);
		if (parts.length < 2) {
			errors.push(`Line ${i + 1}: Expected "address,amount" but got "${line}"`);
			continue;
		}

		const rawAddress = parts[0];
		const rawAmount = parts[1];

		if (!rawAddress || !rawAmount) {
			errors.push(`Line ${i + 1}: Missing address or amount`);
			continue;
		}

		// Validate address format
		if (!/^0x[a-fA-F0-9]{40}$/.test(rawAddress)) {
			errors.push(`Line ${i + 1}: Invalid address "${rawAddress}"`);
			continue;
		}

		// Validate amount — reject scientific notation, decimals, and non-integer strings
		const rawNumber = Number(rawAmount);
		if (
			Number.isNaN(rawNumber) ||
			!Number.isInteger(rawNumber) ||
			rawNumber <= 0 ||
			!/^\d+$/.test(rawAmount)
		) {
			errors.push(`Line ${i + 1}: Invalid amount "${rawAmount}" (must be a positive integer)`);
			continue;
		}
		const amount = rawNumber;

		// Normalize address
		let normalizedAddress: string;
		try {
			normalizedAddress = getAddress(rawAddress);
		} catch {
			errors.push(`Line ${i + 1}: Invalid address "${rawAddress}"`);
			continue;
		}

		// Merge duplicates
		const existing = addressMap.get(normalizedAddress);
		if (existing !== undefined) {
			addressMap.set(normalizedAddress, existing + amount);
			duplicatesMerged++;
		} else {
			addressMap.set(normalizedAddress, amount);
		}
	}

	const valid: AirdropRecipient[] = [];
	for (const [address, amount] of addressMap) {
		valid.push({ address, amount });
	}

	return { valid, errors, duplicatesMerged };
}

/**
 * Validate that the total airdrop amount is within the allowed range.
 * Returns an error message string if invalid, or null if valid.
 */
export function validateAirdropSupply(totalAmount: number): string | null {
	if (totalAmount < MIN_AIRDROP_AMOUNT || totalAmount > MAX_AIRDROP_AMOUNT) {
		return `Airdrop must allocate between ${MIN_AIRDROP_AMOUNT.toLocaleString()} and ${MAX_AIRDROP_AMOUNT.toLocaleString()} tokens (0.25%–90% of supply)`;
	}
	return null;
}

/** Result of Merkle tree generation. */
export type MerkleTreeResult = {
	merkleRoot: `0x${string}`;
	amount: number;
	treeDump: unknown;
};

/**
 * Generate a Merkle tree from the recipient list using the SDK.
 *
 * Uses createMerkleTree from @repo/clanker-sdk which builds a
 * StandardMerkleTree from @openzeppelin/merkle-tree.
 */
export async function generateMerkleTree(
	recipients: AirdropRecipient[]
): Promise<MerkleTreeResult> {
	// Dynamic import to avoid loading the heavy merkle tree library upfront
	const { createMerkleTree } = await import("@repo/clanker-sdk");

	const entries = recipients.map((r) => {
		const lowered = r.address.toLowerCase();
		// Addresses are pre-validated as 0x-prefixed, so template literal preserves the type
		const account: `0x${string}` = `0x${lowered.slice(2)}`;
		return { account, amount: r.amount };
	});

	const { tree, root } = createMerkleTree(entries);
	const amount = recipients.reduce((sum, r) => sum + r.amount, 0);

	return {
		merkleRoot: root,
		amount,
		treeDump: tree.dump(),
	};
}
